// See App.tsx's own comment on this same import -- required for the
// classic JSX transform this app's tsconfig uses.
import * as React from 'react';
import { useEffect, useRef, useState } from 'react';
import type {
  AvailDayReason,
  GuidedChoiceOption,
  GuidedInput,
  GuidedPeriodInfo,
  GuidedText,
  OtherMoneyType,
} from 'employment-insurance-data-access';
import { formatPhone, weekdayLabel, type GuidedTextResolver, type Locale } from './guided-text';

/**
 * The input under the current question, one form per input kind the BFF
 * sends. Each form collects a value and posts it as one `answer` event; it
 * validates nothing beyond what an input element does natively -- the BFF
 * checks every answer and returns any error in the transcript.
 *
 * SCDS components where one exists (scds-button, scds-text-input,
 * scds-currency-input, scds-picker); their values arrive through one
 * delegated `scdsInput`/`scdsChange` listener keyed by `data-field`, as in
 * EiApplicationForm (React 19 doesn't wire `on<CustomEvent>` props). Two
 * native controls, styled with SCDS tokens: date fields, because
 * scds-text-input's date type has no min/max and the dates must be limited
 * to the period, and hours fields, because SCDS has no number input. The
 * option chips are custom: SCDS has no chip.
 */
export interface QuestionInputProps {
  input: GuidedInput;
  /** The current question's own text, for labelling a lone field. */
  question: GuidedText | null;
  period: GuidedPeriodInfo;
  t: GuidedTextResolver;
  locale: Locale;
  busy: boolean;
  /** `loadedLocale`-derived: remounts scds-picker when its option text changes. */
  contentKey: string;
  onAnswer: (value: unknown) => void;
  onFlagResponse: (choice: string) => void;
}

const k = (key: string, params?: GuidedText['params']): GuidedText => (params ? { key, params } : { key });

/** Delegated SCDS field events for one form root. */
function useScdsFields(onField: (field: string, value: unknown) => void) {
  const ref = useRef<HTMLDivElement>(null);
  const handler = useRef(onField);
  handler.current = onField;
  useEffect(() => {
    const root = ref.current;
    if (!root) return;
    const listen = (event: Event) => {
      const field = (event.target as HTMLElement | null)?.getAttribute?.('data-field');
      if (field) handler.current(field, (event as CustomEvent).detail);
    };
    root.addEventListener('scdsInput', listen);
    root.addEventListener('scdsChange', listen);
    return () => {
      root.removeEventListener('scdsInput', listen);
      root.removeEventListener('scdsChange', listen);
    };
  }, []);
  return ref;
}

function ActionButton({
  children,
  onClick,
  disabled,
  variant = 'primary',
  testId,
}: {
  children: React.ReactNode;
  onClick: () => void;
  disabled?: boolean;
  variant?: 'primary' | 'secondary';
  testId?: string;
}) {
  return (
    <scds-button
      variant={variant}
      type="button"
      disabled={disabled}
      data-testid={testId}
      onClick={() => {
        if (!disabled) onClick();
      }}
    >
      {children}
    </scds-button>
  );
}

function Chips({
  options,
  t,
  selected,
  busy,
  onChoose,
}: {
  options: GuidedChoiceOption[];
  t: GuidedTextResolver;
  selected?: string;
  busy: boolean;
  onChoose: (value: string) => void;
}) {
  return (
    <div className="ei-guided__chips">
      {options.map((option) => (
        <button
          key={option.value}
          type="button"
          className={`ei-guided__chip${option.secondary ? ' ei-guided__chip--secondary' : ''}${selected === option.value ? ' is-selected' : ''}`}
          data-testid={`guided-option-${option.value}`}
          disabled={busy}
          onClick={() => onChoose(option.value)}
        >
          {selected === option.value ? <span aria-hidden="true">✓ </span> : null}
          {t(option.text)}
        </button>
      ))}
    </div>
  );
}

function NumberField({
  id,
  label,
  unit,
  value,
  step,
  onChange,
}: {
  id: string;
  label: string;
  unit?: string;
  value: string;
  step: number;
  onChange: (value: string) => void;
}) {
  return (
    <div className="ei-guided__field">
      <label htmlFor={id} className="ei-guided__label">
        {label}
      </label>
      <span className="ei-guided__number">
        <input
          id={id}
          type="number"
          inputMode="decimal"
          min={0}
          step={step}
          value={value}
          data-testid={id}
          onChange={(event) => onChange(event.target.value)}
        />
        {unit ? <span aria-hidden="true">{unit}</span> : null}
      </span>
    </div>
  );
}

function DateField({ id, label, value, period, onChange }: { id: string; label: string; value: string; period: GuidedPeriodInfo; onChange: (value: string) => void }) {
  return (
    <div className="ei-guided__field">
      <label htmlFor={id} className="ei-guided__label">
        {label}
      </label>
      <input
        id={id}
        className="ei-guided__native"
        type="date"
        min={period.firstDay}
        max={period.lastDay}
        value={value}
        data-testid={id}
        onChange={(event) => onChange(event.target.value)}
      />
    </div>
  );
}

const HOURS = Array.from({ length: 24 }, (_, i) => String(i).padStart(2, '0'));
const MINUTES = Array.from({ length: 12 }, (_, i) => String(i * 5).padStart(2, '0'));

/** 24-hour hour and minute selects. */
function TimeField({ id, label, value, t, onChange }: { id: string; label: string; value: string; t: GuidedTextResolver; onChange: (value: string) => void }) {
  const [hour = '', minute = ''] = value ? value.split(':') : [];
  const set = (h: string, m: string) => onChange(h && m ? `${h}:${m}` : h || m ? `${h}:${m}` : '');
  return (
    <fieldset className="ei-guided__time">
      <legend className="ei-guided__label">{label}</legend>
      <label className="ei-guided__visually-hidden" htmlFor={`${id}-hour`}>
        {t(k('field.hour'))}
      </label>
      <select id={`${id}-hour`} className="ei-guided__native" data-testid={`${id}-hour`} value={hour} onChange={(e) => set(e.target.value, minute || '00')}>
        <option value="">--</option>
        {HOURS.map((h) => (
          <option key={h} value={h}>
            {h}
          </option>
        ))}
      </select>
      <span aria-hidden="true">:</span>
      <label className="ei-guided__visually-hidden" htmlFor={`${id}-minute`}>
        {t(k('field.minute'))}
      </label>
      <select id={`${id}-minute`} className="ei-guided__native" data-testid={`${id}-minute`} value={minute} onChange={(e) => set(hour, e.target.value)}>
        <option value="">--</option>
        {MINUTES.map((m) => (
          <option key={m} value={m}>
            {m}
          </option>
        ))}
      </select>
    </fieldset>
  );
}

const num = (value: string) => (value === '' ? 0 : Number(value));
const str = (value: number | undefined | null) => (value === undefined || value === null || value === 0 ? '' : String(value));

export function QuestionInput(props: QuestionInputProps) {
  const { input, t, busy, onAnswer, onFlagResponse } = props;
  switch (input.kind) {
    case 'choice':
      return <Chips options={input.options} t={t} selected={input.selected} busy={busy} onChoose={onAnswer} />;
    case 'flag':
      return <Chips options={input.options} t={t} busy={busy} onChoose={onFlagResponse} />;
    case 'travelDates':
      return <TravelDatesForm {...props} input={input} />;
    case 'travelReason':
      return <TravelReasonForm {...props} input={input} />;
    case 'week':
      return <WeekForm {...props} input={input} />;
    case 'employerPhones':
      return <EmployerPhonesForm {...props} input={input} />;
    case 'text':
      return <TextForm {...props} input={input} />;
    case 'date':
      return <SingleDateForm {...props} input={input} />;
    case 'trainingWeek':
      return <TrainingWeekForm {...props} input={input} />;
    case 'weekdays':
      return <WeekdaysForm {...props} input={input} />;
    case 'perDayReasons':
      return <PerDayForm {...props} input={input} />;
    case 'amount':
      return <AmountForm {...props} input={input} />;
    case 'otherTypes':
      return <OtherTypesForm {...props} input={input} />;
    case 'otherDetails':
      return <OtherDetailsForm {...props} input={input} />;
  }
}

type FormProps<K extends GuidedInput['kind']> = QuestionInputProps & { input: Extract<GuidedInput, { kind: K }> };

function TravelDatesForm({ input, t, period, busy, onAnswer }: FormProps<'travelDates'>) {
  const v = input.values;
  const [leftDate, setLeftDate] = useState(v.travelLeftDate ?? '');
  const [leftTime, setLeftTime] = useState(v.travelLeftTime ?? '');
  const [backDate, setBackDate] = useState(v.travelBackDate ?? '');
  const [backTime, setBackTime] = useState(v.travelBackTime ?? '');
  return (
    <div className="ei-guided__form">
      <div className="ei-guided__form-row">
        <div className="ei-guided__pair">
          <DateField id="guided-left-date" label={t(k('field.leftDate'))} value={leftDate} period={period} onChange={setLeftDate} />
          <TimeField id="guided-left-time" label={t(k('field.leftTime'))} value={leftTime} t={t} onChange={setLeftTime} />
        </div>
        <div className="ei-guided__pair">
          <DateField id="guided-back-date" label={t(k('field.backDate'))} value={backDate} period={period} onChange={setBackDate} />
          <TimeField id="guided-back-time" label={t(k('field.backTime'))} value={backTime} t={t} onChange={setBackTime} />
        </div>
      </div>
      <ActionButton testId="guided-submit" disabled={busy} onClick={() => onAnswer({ leftDate, leftTime, backDate, backTime })}>
        {t(k('btn.next'))}
      </ActionButton>
    </div>
  );
}

function TravelReasonForm({ input, t, busy, contentKey, onAnswer }: FormProps<'travelReason'>) {
  const [reason, setReason] = useState<string>(input.values.travelReason ?? '');
  const [other, setOther] = useState(input.values.travelReasonOther ?? '');
  const ref = useScdsFields((field, value) => {
    if (field === 'travelReason') setReason(String(value ?? ''));
    if (field === 'travelReasonOther') setOther(String(value ?? ''));
  });
  return (
    <div className="ei-guided__form" ref={ref}>
      <scds-picker
        key={`travelReason-${contentKey}`}
        data-field="travelReason"
        display="select"
        label={t(k('field.travelReason'))}
        placeholder={t(k('field.travelReasonPlaceholder'))}
        value={reason}
      >
        {input.options.map((option) => (
          <scds-picker-option key={option.value} value={option.value}>
            {t(option.text)}
          </scds-picker-option>
        ))}
      </scds-picker>
      {reason === 'other' ? (
        <scds-text-input data-field="travelReasonOther" label={t(k('field.pleaseSpecify'))} value={other} />
      ) : null}
      <ActionButton testId="guided-submit" disabled={busy} onClick={() => onAnswer({ reason, other })}>
        {t(k('btn.addIt'))}
      </ActionButton>
    </div>
  );
}

function WeekForm({ input, t, busy, onAnswer }: FormProps<'week'>) {
  const v = input.values;
  const [hours, setHours] = useState(str(v.hours));
  const [earnings, setEarnings] = useState<number | null>(v.earnings ?? null);
  const [selfEmployed, setSelfEmployed] = useState(v.selfEmployed ?? false);
  const [selfEmployment, setSelfEmployment] = useState<number | null>(v.selfEmploymentEarnings ?? null);
  const ref = useScdsFields((field, value) => {
    if (field === 'earnings') setEarnings(value as number | null);
    if (field === 'selfEmploymentEarnings') setSelfEmployment(value as number | null);
  });
  return (
    <div className="ei-guided__form" ref={ref}>
      <div className="ei-guided__form-row">
        <NumberField id={`guided-hours-week${input.week}`} label={t(k('field.hours'))} unit={t(k('field.hoursUnit'))} value={hours} step={0.25} onChange={setHours} />
        <scds-currency-input data-field="earnings" label={t(k('field.grossEarnings'))} min={0} value={earnings ?? undefined} />
      </div>
      <div>
        <button
          type="button"
          className={`ei-guided__chip${selfEmployed ? ' is-selected' : ''}`}
          aria-pressed={selfEmployed}
          data-testid="guided-self-employed"
          onClick={() => setSelfEmployed((was) => !was)}
        >
          {t(k('field.selfEmployed'))}
        </button>
      </div>
      {selfEmployed ? (
        <scds-currency-input data-field="selfEmploymentEarnings" label={t(k('field.selfEmploymentEarnings'))} min={0} value={selfEmployment ?? undefined} />
      ) : null}
      <ActionButton
        testId="guided-submit"
        disabled={busy}
        onClick={() =>
          onAnswer({ hours: num(hours), earnings: earnings ?? 0, selfEmployed, selfEmploymentEarnings: selfEmployed ? (selfEmployment ?? 0) : 0 })
        }
      >
        {t(k(input.week === 1 ? 'btn.confirmWeek1' : 'btn.confirmWeek2'))}
      </ActionButton>
    </div>
  );
}

function EmployerPhonesForm({ input, t, locale, busy, onAnswer }: FormProps<'employerPhones'>) {
  // Stored as digits (or as an older formatted value): show them as the citizen
  // would type them in this locale. The BFF takes any format back.
  const [phones, setPhones] = useState<string[]>(input.values.length > 0 ? input.values.map((phone) => formatPhone(phone, locale)) : ['']);
  const ref = useScdsFields((field, value) => {
    const match = /^phone-(\d+)$/.exec(field);
    if (match) {
      const i = Number(match[1]);
      setPhones((list) => list.map((p, j) => (j === i ? String(value ?? '') : p)));
    }
  });
  return (
    <div className="ei-guided__form" ref={ref}>
      {phones.map((phone, i) => (
        <div className="ei-guided__phone-row" key={i}>
          <scds-text-input
            data-field={`phone-${i}`}
            type="tel"
            autocomplete="tel"
            maxlength={14}
            label={t(k('field.employerPhone', { n: { t: 'number', v: i + 1 } }))}
            placeholder={t(k('field.phonePlaceholder'))}
            value={phone}
          />
          {phones.length > 1 ? (
            <button
              type="button"
              className="ei-guided__remove"
              aria-label={t(k('btn.removeEmployer', { n: { t: 'number', v: i + 1 } }))}
              onClick={() => setPhones((list) => list.filter((_, j) => j !== i))}
            >
              <span aria-hidden="true">✕</span>
            </button>
          ) : null}
        </div>
      ))}
      <button type="button" className="ei-guided__link-button" data-testid="guided-add-employer" onClick={() => setPhones((list) => [...list, ''])}>
        <span aria-hidden="true">+ </span>
        {t(k('btn.addEmployer'))}
      </button>
      <ActionButton testId="guided-submit" disabled={busy} onClick={() => onAnswer(phones)}>
        {t(k('btn.addEmployers'))}
      </ActionButton>
    </div>
  );
}

function TextForm({ input, question, t, busy, onAnswer }: FormProps<'text'>) {
  const [value, setValue] = useState(input.value);
  const ref = useScdsFields((_field, v) => setValue(String(v ?? '')));
  return (
    <div className="ei-guided__form" ref={ref}>
      <scds-text-input
        data-field={input.field}
        label={question ? t(question) : t(k('answerLabel'))}
        placeholder={t(k(input.field === 'stopReasonOther' ? 'field.stopOtherPlaceholder' : 'field.allowanceFromPlaceholder'))}
        value={value}
      />
      <ActionButton testId="guided-submit" disabled={busy} onClick={() => onAnswer(value)}>
        {t(k('btn.addIt'))}
      </ActionButton>
    </div>
  );
}

function SingleDateForm({ input, t, period, busy, onAnswer }: FormProps<'date'>) {
  const [value, setValue] = useState(input.value);
  return (
    <div className="ei-guided__form">
      <DateField id="guided-date" label={t(k('field.fullTimeDate'))} value={value} period={period} onChange={setValue} />
      <ActionButton testId="guided-submit" disabled={busy} onClick={() => onAnswer(value)}>
        {t(k('btn.confirmDate'))}
      </ActionButton>
    </div>
  );
}

function TrainingWeekForm({ input, t, busy, onAnswer }: FormProps<'trainingWeek'>) {
  const [hours, setHours] = useState(str(input.values.hours));
  const [allowance, setAllowance] = useState<number | null>(input.values.allowance ?? null);
  const ref = useScdsFields((field, value) => {
    if (field === 'allowance') setAllowance(value as number | null);
  });
  return (
    <div className="ei-guided__form" ref={ref}>
      <div className="ei-guided__form-row">
        <NumberField id={`guided-training-hours-week${input.week}`} label={t(k('field.hoursAttended'))} unit={t(k('field.hoursUnit'))} value={hours} step={0.25} onChange={setHours} />
        <scds-currency-input data-field="allowance" label={t(k('field.trainingAllowance'))} min={0} value={allowance ?? undefined} />
      </div>
      <div className="ei-guided__actions">
        <ActionButton testId="guided-submit" disabled={busy} onClick={() => onAnswer({ hours: num(hours), allowance: allowance ?? 0 })}>
          {t(k(input.week === 1 ? 'btn.confirmWeek1' : 'btn.confirmWeek2'))}
        </ActionButton>
        <ActionButton variant="secondary" testId="guided-no-training" disabled={busy} onClick={() => onAnswer({ hours: 0, allowance: 0 })}>
          {t(k(`opt.noTraining.week${input.week}`))}
        </ActionButton>
      </div>
    </div>
  );
}

function WeekdaysForm({ input, t, locale, busy, onAnswer }: FormProps<'weekdays'>) {
  const [selected, setSelected] = useState<string[]>(input.selected);
  const toggle = (day: string) => setSelected((list) => (list.includes(day) ? list.filter((d) => d !== day) : [...list, day].sort()));
  return (
    <div className="ei-guided__form">
      <div className="ei-guided__chips" role="group" aria-label={t(k('answerLabel'))}>
        {input.days.map((day) => (
          <button
            key={day}
            type="button"
            className={`ei-guided__chip${selected.includes(day) ? ' is-selected' : ''}`}
            aria-pressed={selected.includes(day)}
            data-testid={`guided-day-${day}`}
            onClick={() => toggle(day)}
          >
            {weekdayLabel(day, locale)}
          </button>
        ))}
      </div>
      <p className="ei-guided__hint">
        {selected.length > 0 ? t(k('answer.days', { days: { t: 'dayList', v: selected } })) : t(k('field.dayHint'))}
      </p>
      <ActionButton testId="guided-submit" disabled={busy} onClick={() => onAnswer(selected)}>
        {t(k('btn.continue'))}
      </ActionButton>
    </div>
  );
}

const DAY_REASONS: AvailDayReason[] = ['illness', 'outsideCanada', 'other'];

function PerDayForm({ input, t, busy, contentKey, onAnswer }: FormProps<'perDayReasons'>) {
  const [values, setValues] = useState<Record<string, string>>(input.values);
  const ref = useScdsFields((field, value) => {
    if (field.startsWith('reason-')) setValues((v) => ({ ...v, [field.slice('reason-'.length)]: String(value ?? '') }));
  });
  return (
    <div className="ei-guided__form" ref={ref}>
      {input.days.map((day) => (
        <scds-picker
          key={`reason-${day}-${contentKey}`}
          data-field={`reason-${day}`}
          display="select"
          label={t(k('field.perDayLabel', { day: { t: 'shortDate', v: day } }))}
          placeholder={t(k('field.perDaySelect'))}
          value={values[day] ?? ''}
        >
          {DAY_REASONS.map((reason) => (
            <scds-picker-option key={reason} value={reason}>
              {t(k(`opt.availReason.${reason}`))}
            </scds-picker-option>
          ))}
        </scds-picker>
      ))}
      <ActionButton testId="guided-submit" disabled={busy} onClick={() => onAnswer(values)}>
        {t(k('btn.continue'))}
      </ActionButton>
    </div>
  );
}

function AmountForm({ input, t, busy, onAnswer }: FormProps<'amount'>) {
  const [value, setValue] = useState<number | null>(input.value);
  const ref = useScdsFields((_field, v) => setValue(v as number | null));
  return (
    <div className="ei-guided__form" ref={ref}>
      <scds-currency-input data-field="amount" label={t(k('field.wliAmount', { week: { t: 'number', v: input.week } }))} min={0} value={value ?? undefined} />
      <ActionButton testId="guided-submit" disabled={busy} onClick={() => onAnswer(value ?? 0)}>
        {t(k('btn.addAmount'))}
      </ActionButton>
    </div>
  );
}

const MONEY_TYPES: OtherMoneyType[] = ['vacationPay', 'severance', 'bonus', 'pension', 'other'];

function OtherTypesForm({ input, t, busy, onAnswer }: FormProps<'otherTypes'>) {
  const [selected, setSelected] = useState<OtherMoneyType[]>(input.selected);
  const [description, setDescription] = useState(input.otherDescription);
  const ref = useScdsFields((_field, v) => setDescription(String(v ?? '')));
  const toggle = (type: OtherMoneyType) =>
    setSelected((list) => (list.includes(type) ? list.filter((x) => x !== type) : MONEY_TYPES.filter((x) => x === type || list.includes(x))));
  return (
    <div className="ei-guided__form" ref={ref}>
      <div className="ei-guided__chips" role="group" aria-label={t(k('answerLabel'))}>
        {MONEY_TYPES.map((type) => (
          <button
            key={type}
            type="button"
            className={`ei-guided__chip${type === 'other' ? ' ei-guided__chip--secondary' : ''}${selected.includes(type) ? ' is-selected' : ''}`}
            aria-pressed={selected.includes(type)}
            data-testid={`guided-money-${type}`}
            onClick={() => toggle(type)}
          >
            {t(k(`opt.moneyType.${type}`))}
          </button>
        ))}
      </div>
      {selected.includes('other') ? (
        <scds-text-input
          data-field="otherDescription"
          label={t(k('field.otherDescribe'))}
          placeholder={t(k('field.otherDescribePlaceholder'))}
          value={description}
        />
      ) : null}
      <ActionButton testId="guided-submit" disabled={busy} onClick={() => onAnswer({ types: selected, otherDescription: description })}>
        {selected.length > 1 ? t(k('btn.continueCount', { count: { t: 'number', v: selected.length } })) : t(k('btn.continue'))}
      </ActionButton>
    </div>
  );
}

function OtherDetailsForm({ input, t, busy, onAnswer }: FormProps<'otherDetails'>) {
  const [from, setFrom] = useState(input.values.from ?? '');
  const [amount, setAmount] = useState<number | null>(input.values.amount ? input.values.amount : null);
  const ref = useScdsFields((field, v) => {
    if (field === 'from') setFrom(String(v ?? ''));
    if (field === 'amount') setAmount(v as number | null);
  });
  return (
    <div className="ei-guided__form" ref={ref}>
      <scds-text-input data-field="from" label={t(k('field.fromWhom'))} placeholder={t(k('field.fromPlaceholder'))} value={from} />
      <scds-currency-input data-field="amount" label={t(k('field.amountReceived'))} min={0} value={amount ?? undefined} />
      <ActionButton testId="guided-submit" disabled={busy} onClick={() => onAnswer({ from, amount: amount ?? 0 })}>
        {t(k(input.index < input.count - 1 ? 'btn.next' : 'btn.addIt'))}
      </ActionButton>
    </div>
  );
}
